/**
 * Выбор подложки карты.
 *
 * Провайдер держится сменной деталью: код карты — метки, линия ответа,
 * подгонка масштаба — от него не зависит и одинаков для любой подложки.
 * Поэтому переход на другого поставщика тайлов остаётся правкой переменной
 * окружения, а не переписыванием компонента.
 *
 * Вариантов два: векторные тайлы OpenFreeMap по умолчанию и растровая
 * подложка Яндекса, если задан ключ Tiles API. Модуль повторяет такой же
 * в игровом фронтенде — репозитории независимы, и общей библиотеки у них нет.
 */
import type { StyleSpecification } from "maplibre-gl";

/** Стиль векторной подложки. Кроме positron: bright, liberty, fiord, dark. */
const VECTOR_STYLE =
  import.meta.env.VITE_MAP_STYLE_URL ?? "https://tiles.openfreemap.org/styles/positron";

const YANDEX_KEY = import.meta.env.VITE_YANDEX_TILES_KEY ?? "";
const YANDEX_TILES = "https://tiles.api-maps.yandex.ru/v1/tiles/";

/**
 * Яндекс требует показывать свой копирайт поверх карты. MapLibre берёт текст
 * из поля attribution источника и рисует его в углу.
 */
const YANDEX_ATTRIBUTION =
  '<a href="https://yandex.ru/maps/" target="_blank" rel="noreferrer">© Яндекс Карты</a>';

/**
 * Растровая подложка Яндекса.
 *
 * Ключ уезжает в бандл и виден любому: запросы за тайлами идут прямо из
 * браузера игрока, спрятать его негде. Ограничение по referrer'у в кабинете
 * Яндекса — единственное, что мешает пользоваться им с чужих сайтов.
 */
function yandexStyle(key: string): StyleSpecification {
  // Экраны с высокой плотностью пикселей получают удвоенный тайл, иначе
  // растровая карта выглядит мыльной рядом с чётким интерфейсом.
  const scale = typeof window !== "undefined" && window.devicePixelRatio > 1 ? 2 : 1;
  const query = `l=map&x={x}&y={y}&z={z}&lang=ru_RU&scale=${scale}&apikey=${encodeURIComponent(key)}`;

  return {
    version: 8,
    sources: {
      yandex: {
        type: "raster",
        tiles: [`${YANDEX_TILES}?${query}`],
        // Размер описывает не картинку, а её охват на местности: при scale=2
        // приходит вдвое более подробное изображение того же квадрата.
        tileSize: 256,
        maxzoom: 19,
        attribution: YANDEX_ATTRIBUTION,
      },
    },
    layers: [{ id: "yandex", type: "raster", source: "yandex" }],
  };
}

/** Подложка для карты: ключ Яндекса включает растр, иначе вектор. */
export function mapStyle(): StyleSpecification | string {
  return YANDEX_KEY ? yandexStyle(YANDEX_KEY) : VECTOR_STYLE;
}

/**
 * Работаем ли на тайлах Яндекса: их условия требуют показывать логотип
 * поверх карты и делать его ссылкой на Яндекс Карты.
 */
export const usesYandexTiles = Boolean(YANDEX_KEY);
